/**
 * Works out where an in-app message's button should send the user.
 *
 * Mobile handles `deepLink` by rewriting the URL to a private scheme and handing it to the
 * app's own router (AppMessage.kt on Android, AppMessageManager.handleURL on iOS). A
 * browser has no equivalent, so the web reading is "stay inside the app": a deep link
 * pointing at our own origin becomes a react-router navigation with no page reload, and
 * everything else opens in a new tab so the user's map session survives.
 *
 * Anything the browser cannot act on resolves to 'none' — a leftover basemapurlscheme://
 * link authored for mobile, or a javascript: URL. The click is still recorded and the
 * popup still closes; the user is just not dropped on a browser error page. Restricting
 * to http/https is also what stops an admin-supplied string from becoming script.
 *
 * `origin` is a parameter rather than a read of window.location so this stays a pure
 * function — the repo's jest suite runs under the node environment, with no window.
 *
 * @param {string} buttonLink Raw ButtonLink from the message. Relative paths are allowed
 *   and resolve against `origin`.
 * @param {boolean} deepLink The message's DeepLink flag.
 * @param {string} origin The origin the app is served from, e.g. window.location.origin.
 * @returns {{kind: 'internal', to: string}|{kind: 'external', href: string}|{kind: 'none'}}
 */
export const resolveTarget = (buttonLink, deepLink, origin) => {
  if (!buttonLink) return { kind: 'none' }

  let url
  try {
    url = new URL(buttonLink, origin)
  } catch {
    return { kind: 'none' }
  }

  if (url.protocol !== 'http:' && url.protocol !== 'https:') return { kind: 'none' }

  if (deepLink && url.origin === origin) {
    return { kind: 'internal', to: `${url.pathname}${url.search}${url.hash}` }
  }

  return { kind: 'external', href: url.href }
}
